import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { PasswordInput } from '../../components/forms/PasswordInput';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../lib/api';
import { pageStyles, cardStyles } from '../../lib/styles';

export function PasswordPage() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = t('validation.required');
    }

    if (!formData.newPassword) {
      newErrors.newPassword = t('validation.required');
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('auth.passwordRequirements');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = t('auth.passwordRequirements');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.required');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await api.put('/api/auth/password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      showSuccess(t('auth.passwordChanged'));
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.passwordChangeFailed');
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={pageStyles.container}>
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">{t('auth.changePassword')}</span>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500">
            {t('auth.passwordRequirements')}
          </p>

          <form onSubmit={handleSubmit} className="mt-4 max-w-md space-y-3">
            <PasswordInput
              label={t('auth.currentPassword')}
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              error={errors.currentPassword}
              autoComplete="current-password"
            />

            <PasswordInput
              label={t('auth.newPassword')}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              error={errors.newPassword}
              showRequirements
              autoComplete="new-password"
            />

            <PasswordInput
              label={t('auth.confirmPassword')}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              autoComplete="new-password"
            />

            <div className="pt-3">
              <Button type="submit" loading={isLoading}>
                {t('auth.changePassword')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
